/* eslint-env browser */
'use strict';
import $ from 'jquery';
import PostcardPreview from 'modules/postcard-preview';
import _ from 'underscore';
import 'jquery.scrollto';

/**
 * The gallery controller.
 */
class Gallery {
  /**
   * Constructor function
   * @constructor
   * @param {HTMLElement} el - component element
   */
  constructor(el) {
    /** @prop {HTMLElement} Component form element. */
    this.el = el;

    /** @prop {jQuery} Component element in a jQuery wrapper. */
    this.$el = $(el);

    /** @private {Array.<Object>} Array of postcard data objects. */
    this._data = [];

    /** @private {number} Current gallery index. */
    this._index = 0;

    /** @private {Boolean} Whether the module is currently loading images. */
    this._isLoading = false;
  }

  /**
   * Initialization function. Binds event handlers and fetches data.
   * @method
   * @return {this} Gallery
   */
  init() {
    this.$el.addClass(Gallery.CssClass.LOADING)
        .on('click', '.gallery-card', e => {
          $(e.delegateTarget).find(`.${Gallery.CssClass.FLIPPED}`)
              .not(e.currentTarget).removeClass(Gallery.CssClass.FLIPPED);
          $(e.currentTarget).toggleClass(Gallery.CssClass.FLIPPED);
        }).on('click', `.${Gallery.CssClass.LOAD_MORE}`, e => {
          e.preventDefault();
          if (this._isLoading) {
            return;
          }

          const $btn = $(e.currentTarget);
          const originalText = $btn.text();
          $btn.text(Gallery.Content.LOADING).prop('disabled', true);

          this.$el.addClass(Gallery.CssClass.LOADING);
          this._loadImages().done(() => {
            this.$el.removeClass(Gallery.CssClass.LOADING);
            $.scrollTo(this.$el, {
              duration: 500,
              offset: -50
            });
            $btn.text(originalText).prop('disabled', false);
          });
        });

    this._fetchData().done(data => {
      this._data = this._shuffleData(data);
      this._loadImages().done(() => {
        this.$el.removeClass(Gallery.CssClass.LOADING);
      });
    });

    return this;
  }

  /**
   * Loads the next set of images and only appends them to the DOM with a fade
   * effect once the images have downloaded.
   * @private
   * @return {jQuery.Deferred} deferred object that resolves when all images
   *   have loaded.
   */
  _loadImages() {
    this._isLoading = true;

    // Determine items to display.
    const DISPLAY_COUNT = 6;
    let newIndex = this._index + DISPLAY_COUNT;
    const dataToDisplay = this._data.slice(this._index, newIndex);

    // If we are at the end of the data, loop around to the beginning.
    if (newIndex > this._data.length) {
      newIndex -= this._data.length;
      dataToDisplay.concat(this._data.slice(0, newIndex));
    }

    // Create an array of deferred objects that will resolve once their
    // associated images download.
    const deferreds = [];

    _.each(dataToDisplay, item => {
      const deferred = new $.Deferred();
      // We are expecting to load two images (sides) for each postcard.
      let loadingSides = 2;
      const $postcard = $(`
        <div class="gallery-card-wrapper"><button class="gallery-card">
          <figure class="gallery-card-side gallery-card-front postcard-front">
            <div class="postcard-image"></div>
          </figure>
          <figure class="gallery-card-side gallery-card-back postcard-back">
            <div class="postcard-image"></div>
          </figure>
        </button></div>`);

      deferreds.push(deferred);
      // Listen twice for the `updated` event from the PostcardPreview object,
      // then resolve this postcard's deferred object.
      $postcard.on('updated', () => {
        loadingSides -= 1;
        if (!loadingSides) {
          // Resolve the deferred object, passing the $postcard element.
          deferred.resolve($postcard);
          $postcard.off('updated');
        }
      });

      // Create a new PostcardPreview object to construct the image URLs and
      // kick off their preloading.
      const postcardPreview = new PostcardPreview($postcard[0]);
      postcardPreview.update({
        'bg-image': item.image || '',
        'city': item.city || '',
        'firstname': item.name || '',
        'postcard-message-1': item.front || '',
        'postcard-message-2': item.back || ''
      });
    });

    // When all deferreds have resolved, append the resolved $postcard elements
    // to the DOM.
    return $.when(...deferreds).done((...postcards) => {
      const $postcardEl = this.$el.find(`.${Gallery.CssClass.COLLECTION}`);
      $postcardEl.fadeOut(300).promise().done(() => {
        $postcardEl.empty();
        _.each(postcards, postcard => {
          $postcardEl.append(postcard);
        });
        return $postcardEl.fadeIn(300).promise();
      }).done(() => {
        this._index = newIndex;
        this._isLoading = false;
      });
    });
  }

  /**
   * Fetches postcard data.
   * @private
   * @return {jQuery.xhr} AJAX response that resolves with JSON data
   */
  _fetchData() {
    return $.getJSON('/json/postcards.json');
  }

  /**
   * Randomizes a passed array of postcard data objects, but placing any
   * "featured" items at the top of the array.
   * @private
   * @param {Array.<Object>} data - Array of postcard data.
   * @return {Array.<Object>} data - Shuffled array of postcard data.
   */
  _shuffleData(data) {
    return _.chain(data).filter(item => item.featured).shuffle().value()
      .concat(_.chain(data).filter(item => !item.featured).shuffle().value());
  }
}

/**
 * CSS classes used by this component.
 * @enum {string}
 */
Gallery.CssClass = {
  CARD: 'gallery-card',
  COLLECTION: 'gallery-collection',
  FLIPPED: 'flipped',
  LOADING: 'loading',
  LOAD_MORE: 'js-gallery-more'
};

/**
 * Text strings used by this component.
 * @enum {string}
 */
Gallery.Content = {
  LOADING: 'Loading'
};

export default Gallery;
